import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconAlertTriangle, IconArrowRight, IconInfoCircle } from '@tabler/icons-react';
import { TeeButton } from '@/components/ui/tee-button';
import { TeeInput } from '@/components/ui/tee-input';
import { TeeCheckbox } from '@/components/ui/tee-checkbox';
import { TeeFormModal } from '@/components/ui/tee-form-modal';
import { TeeLoader } from '@/components/ui/tee-loader';
import { usePlanImpact, useUpdatePlanSettings } from '@/hooks/useGamePlanTracker';
import type { ImpactLine, PlanImpact } from '@/types/gamePlanTracker';

interface EditPlanModalProps {
  isOpen: boolean;
  locationId: string;
  /** The live values, so the form opens on what the owner has today. */
  goal: number;
  grandOpeningDate: string; // yyyy-mm-dd
  onClose: () => void;
}

const GOAL_MIN = 50;
const GOAL_MAX = 150;
const GOAL_STEP = 5;

/**
 * Edit the two inputs the whole Game Plan derives from, with the blast radius
 * shown before the owner commits.
 *
 * The goal and the Grand Opening date are not settings — they are the plan.
 * Nudging either one re-targets every phase, re-times every week, and re-buckets
 * work already logged. So the editor is built around a dry run: each change
 * fires a server-side impact preview and the panel below fills in with the
 * before → after of everything that moves, plus plain-language notes on what
 * happens to existing activity. Nothing is written until Save.
 */
export function EditPlanModal({
  isOpen,
  locationId,
  goal,
  grandOpeningDate,
  onClose,
}: EditPlanModalProps) {
  const { t } = useTranslation();
  const [draftGoal, setDraftGoal] = useState(goal);
  const [draftDate, setDraftDate] = useState(grandOpeningDate);
  const [confirmed, setConfirmed] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Re-seed from the live plan each time the drawer opens, so a cancelled edit
  // never leaks into the next one.
  useEffect(() => {
    if (!isOpen) return;
    setDraftGoal(goal);
    setDraftDate(grandOpeningDate);
    setConfirmed(false);
    setSaveError(null);
  }, [isOpen, goal, grandOpeningDate]);

  // Debounce the date field — typing "2027-03-01" would otherwise fire a
  // preview for every half-formed year. The stepper is debounced with it so
  // held-down clicks resolve to one request.
  const [debounced, setDebounced] = useState({ goal: draftGoal, date: draftDate });
  useEffect(() => {
    const id = setTimeout(() => setDebounced({ goal: draftGoal, date: draftDate }), 350);
    return () => clearTimeout(id);
  }, [draftGoal, draftDate]);

  const dirty = draftGoal !== goal || draftDate !== grandOpeningDate;
  const dateWellFormed = /^\d{4}-\d{2}-\d{2}$/.test(debounced.date);

  const {
    data: impact,
    isFetching,
    error,
  } = usePlanImpact(
    locationId,
    debounced.goal,
    debounced.date,
    isOpen && dateWellFormed,
  );

  const update = useUpdatePlanSettings(locationId);

  // Only a preview that matches what is actually on screen may gate Save.
  const previewIsCurrent =
    debounced.goal === draftGoal && debounced.date === draftDate && !isFetching;
  const needsConfirm = Boolean(impact?.requiresCompressedConfirm);
  const previewError = error ? messageOf(error) : null;

  const canSave =
    dirty &&
    dateWellFormed &&
    !previewError &&
    previewIsCurrent &&
    (!needsConfirm || confirmed);

  const save = async () => {
    setSaveError(null);
    try {
      await update.mutateAsync({
        foundingMemberGoal: draftGoal,
        grandOpeningDate: draftDate,
        confirmCompressed: needsConfirm ? confirmed : undefined,
      });
      onClose();
    } catch (e) {
      setSaveError(messageOf(e));
    }
  };

  return (
    <TeeFormModal
      isOpen={isOpen}
      title={t('gamePlan.editPlanTitle', 'Adjust your Game Plan')}
      onClose={onClose}
      primaryActionLabel={t('gamePlan.editPlanSave', 'Save changes')}
      onPrimaryAction={save}
      primaryActionDisabled={!canSave}
      isLoading={update.isPending}
      dismissOnBackdropClick={false}
      dismissOnEscape={false}
    >
      <div className="flex flex-col gap-6">
        <p className="text-body-s text-[var(--text-content-sub-title)]">
          {t(
            'gamePlan.editPlanIntro',
            'Your goal and your Grand Opening date drive everything in Game Plan — phase targets, weekly pace, the to-do list and the Goal Board. Change either one and the plan rebuilds itself. Nothing you have logged is deleted.',
          )}
        </p>

        {/* ---- Inputs ---- */}
        <div className="flex flex-col gap-2">
          <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[var(--text-content-sub-title)]">
            {t('gamePlan.editPlanGoalLabel', 'Founding member goal')}
          </span>
          <div className="flex items-center gap-4">
            <TeeButton
              variant="secondary"
              size="m"
              text="–"
              onClick={() => setDraftGoal((g) => Math.max(GOAL_MIN, g - GOAL_STEP))}
              disabled={draftGoal <= GOAL_MIN}
            />
            <span className="w-[86px] text-center text-[34px] font-semibold leading-none text-[var(--color-forest)]">
              {draftGoal}
            </span>
            <TeeButton
              variant="secondary"
              size="m"
              text="+"
              onClick={() => setDraftGoal((g) => Math.min(GOAL_MAX, g + GOAL_STEP))}
              disabled={draftGoal >= GOAL_MAX}
            />
            {draftGoal !== goal && (
              <span className="text-[12px] text-[var(--text-content-sub-title)]">
                {t('gamePlan.editPlanGoalWas', 'was {{goal}}', { goal })}
              </span>
            )}
          </div>
        </div>

        <TeeInput
          label={t('gamePlan.editPlanDateLabel', 'Grand Opening date')}
          type="date"
          value={draftDate}
          onChange={(e) => setDraftDate((e.target as HTMLInputElement).value)}
          destructive={Boolean(previewError)}
          hint={
            previewError ??
            (draftDate !== grandOpeningDate
              ? t('gamePlan.editPlanDateWas', 'was {{date}}', { date: grandOpeningDate })
              : '')
          }
        />

        {/* ---- Impact ---- */}
        <div className="flex flex-col gap-3 rounded-[10px] border border-[var(--border-default)] bg-[var(--background-page-header)] p-4">
          <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[var(--text-content-sub-title)]">
            {t('gamePlan.editPlanImpactTitle', 'What this changes')}
          </span>

          {!dirty ? (
            <p className="text-body-s text-[var(--text-content-sub-title)]">
              {t(
                'gamePlan.editPlanNoChangeYet',
                'Nothing yet — move the goal or the date to see the impact.',
              )}
            </p>
          ) : previewError ? (
            <p className="text-body-s text-[var(--text-badge-red)]">{previewError}</p>
          ) : !impact || isFetching ? (
            <TeeLoader
              size="sm"
              text={t('gamePlan.editPlanImpactLoading', 'Working out the impact…')}
            />
          ) : (
            <ImpactPanel impact={impact} />
          )}
        </div>

        {needsConfirm && dirty && !previewError && (
          <TeeCheckbox
            checked={confirmed}
            onChange={setConfirmed}
            label={t(
              'gamePlan.editPlanConfirmCondensed',
              'I understand this is a condensed plan with a higher weekly pace.',
            )}
          />
        )}

        {saveError && (
          <p className="text-body-s text-[var(--text-badge-red)]">{saveError}</p>
        )}
      </div>
    </TeeFormModal>
  );
}

/** The before → after table plus the plain-language consequences. */
function ImpactPanel({ impact }: { impact: PlanImpact }) {
  const { t } = useTranslation();
  const changedLines = useMemo(
    () => impact.lines.filter((l) => l.direction !== 'same'),
    [impact.lines],
  );
  const unchangedCount = impact.lines.length - changedLines.length;

  return (
    <div className="flex flex-col gap-3">
      {changedLines.length === 0 ? (
        <p className="text-body-s text-[var(--text-content-sub-title)]">
          {t(
            'gamePlan.editPlanNoMaterialChange',
            'The numbers land in the same place — nothing material moves.',
          )}
        </p>
      ) : (
        <div className="flex flex-col divide-y divide-[var(--border-default)] overflow-hidden rounded-[8px] border border-[var(--border-default)] bg-[var(--background-card)]">
          {changedLines.map((l) => (
            <ImpactRow key={l.key} line={l} />
          ))}
        </div>
      )}

      {unchangedCount > 0 && changedLines.length > 0 && (
        <span className="text-[12px] text-[var(--text-content-sub-title)]">
          {t('gamePlan.editPlanUnchangedCount', {
            count: unchangedCount,
            defaultValue_one: '{{count}} other figure stays the same.',
            defaultValue_other: '{{count}} other figures stay the same.',
          })}
        </span>
      )}

      {impact.notes.map((n, i) => (
        <div
          key={i}
          className={
            'flex gap-2 rounded-[8px] px-3 py-2 text-[13px] leading-relaxed ' +
            (n.severity === 'warning'
              ? 'bg-[rgba(181,143,45,0.12)] text-[var(--color-text-gold)]'
              : 'bg-[rgba(12,61,45,0.06)] text-[var(--color-forest)]')
          }
        >
          <span className="mt-[2px] shrink-0">
            {n.severity === 'warning' ? (
              <IconAlertTriangle size={15} />
            ) : (
              <IconInfoCircle size={15} />
            )}
          </span>
          <span>{n.text}</span>
        </div>
      ))}

      {/* Phase targets are the clearest picture of a goal change — show the
          whole ramp rather than only the end number. */}
      <div className="flex flex-col gap-1.5">
        <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[var(--text-content-sub-title)]">
          {t('gamePlan.editPlanPhaseTargets', 'Member targets by phase')}
        </span>
        <div className="flex flex-wrap items-center gap-2">
          {impact.proposed.phaseTargets.map((p, i) => {
            const before = impact.current.phaseTargets[i]?.target;
            const moved = before !== p.target;
            return (
              <div key={p.key} className="flex items-center gap-2">
                <div className="flex flex-col items-center">
                  <span
                    className={
                      'text-[18px] font-bold leading-none ' +
                      (moved
                        ? 'text-[var(--color-text-gold)]'
                        : 'text-[var(--text-content-title)]')
                    }
                  >
                    {p.target}
                  </span>
                  <span className="mt-0.5 text-[10px] text-[var(--text-content-sub-title)]">
                    {p.label}
                  </span>
                </div>
                {i < impact.proposed.phaseTargets.length - 1 && (
                  <span className="text-[var(--color-gold-500)]">→</span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function ImpactRow({ line }: { line: ImpactLine }) {
  return (
    <div className="flex items-center justify-between gap-3 px-3 py-2.5">
      <span className="text-[13px] text-[var(--text-content-sub-title)]">
        {line.label}
      </span>
      <span className="flex items-center gap-2 text-[13px]">
        <span className="text-[var(--text-content-sub-title)] line-through">
          {line.from}
        </span>
        <IconArrowRight size={13} className="text-[var(--color-gold-500)]" />
        <span
          className={
            'font-semibold ' +
            (line.harder
              ? 'text-[var(--color-text-gold)]'
              : 'text-[var(--color-forest)]')
          }
        >
          {line.to}
        </span>
      </span>
    </div>
  );
}

function messageOf(e: unknown): string {
  const res = (e as { response?: { data?: { message?: string | string[] } } })?.response;
  const msg = res?.data?.message;
  if (Array.isArray(msg)) return msg[0];
  if (typeof msg === 'string') return msg;
  return (e as Error)?.message ?? 'Something went wrong.';
}
